import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Col, Row } from "reactstrap";
import { message, Popover } from "antd";
import { EditOutlined } from "@ant-design/icons";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormTextArea from "../../form-components/TextAreaComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { WorkRequestFormData, RequestForType } from "../types";
import usersApis from "../../../api/master/users";
import dropdownApis from "../../../api/WorkOrderManagement/DropdownApis";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import WorkRequestApprovalDrawer from "./WorkRequestApprovalDrawer";
import WorkRequestApprovalViewDrawer from "./WorkRequestApprovalViewDrawer";

interface SelectOptions {
  label: string;
  value: string;
}

const WorkRequestGeneralDetails: React.FC = (props: any) => {
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    register,
  } = useFormContext<WorkRequestFormData>();

  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOptions[]>([]);
  const [loading, setLoading] = useState({
    sites: false,
    projects: false,
  });
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);
  const [showViewApprovals, setShowViewApprovals] = useState(false);
  const [isFormDataFetched, setIsFormDataFetched] = useState(false);

  // Watch form values
  const siteIdWatch = watch("siteId");
  const plannedStartDateWatch = watch("plannedStartDate");
  const approvalRequiredWatch = watch("approvalRequired");
  const AllFormDetails = watch();

  // Request For options - Exact FSD Enum values
  const requestForOptions: SelectOptions[] = [
    { label: "Construction Work", value: RequestForType.ConstructionWork },
    { label: "Service", value: RequestForType.Service },
    { label: "Maintenance", value: RequestForType.Maintenance },
    { label: "Others", value: RequestForType.Others },
  ];

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    // Auto-generate Work Request # if not already set and not in edit mode
    if (!AllFormDetails.workRequestNumber && AllFormDetails.type !== "edit") {
      const timestamp = Date.now().toString();
      const randomNum = Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, "0");
      const wrNumber = `WR-${timestamp.slice(-6)}`;
      setValue("workRequestNumber", wrNumber);
    }
  }, [AllFormDetails.type, AllFormDetails.workRequestNumber, setValue]);

  useEffect(() => {
    // Fetch projects when site changes
    if (siteIdWatch && siteIdWatch !== "") {
      fetchProjectsBySite(siteIdWatch);
      // Clear project selection when site changes (except for edit mode)
      if (AllFormDetails.type !== "edit") {
        setValue("projectId", "");
        setValue("projectName", "");
      }
    }
  }, [siteIdWatch, AllFormDetails.type, setValue]);

  const fetchMasterData = async () => {
    try {
      setLoading((prev) => ({ ...prev, sites: true }));

      const data = await usersApis.getCurrentUser();
      if (data) {
        const sitesOptions = data?.siteAccessibility?.map((item: any) => ({
          label: item.siteName,
          value: item.siteID.toString(),
        }));
        setSitesOptions(sitesOptions);
      }
    } catch (error) {
      console.log("Error fetching sites:", error);
      message.error("Failed to load sites data");
    } finally {
      setLoading((prev) => ({ ...prev, sites: false }));
    }
  };

  const fetchProjectsBySite = async (siteId: string) => {
    try {
      setLoading((prev) => ({ ...prev, projects: true }));

      const { data, status } = await dropdownApis.getProjectsDropdown(
        parseInt(siteId)
      );
      if (status && data) {
        const projectOptions = data.map((item: any) => ({
          label: item?.projectTitle,
          value: item?.projectId?.toString(),
        }));
        setProjectOptions(projectOptions);
      }

      // Update site name in form
      const selectedSite = sitesOptions.find((site) => site.value === siteId);
      if (selectedSite) {
        setValue("siteName", selectedSite.label);
      }
    } catch (error) {
      console.log("Error fetching projects:", error);
      message.error("Failed to load projects data");
    } finally {
      setLoading((prev) => ({ ...prev, projects: false }));
    }
  };

  const handleProjectChange = (projectId: string) => {
    if (projectId) {
      const selectedProject = projectOptions.find(
        (project) => project.value === projectId
      );
      if (selectedProject) {
        setValue("projectName", selectedProject.label);
      }
    } else {
      setValue("projectName", "");
    }
  };

  const setFormValues = () => {
    const { approvalsData } = AllFormDetails;

    if (approvalsData) {
      const _approvals = JSON.parse(approvalsData);
      setApprovals(_approvals);
    }
    if (AllFormDetails.isStrictApproval && AllFormDetails.approvalRequired === "true") {
      setIsStrictApproval(AllFormDetails.isStrictApproval === "true");
    } else {
      setIsStrictApproval(false);
    }
  };

  useEffect(() => {
    if (!isFormDataFetched && Object.keys(AllFormDetails).length > 0) {
      setFormValues();
      setIsFormDataFetched(true);
    }
  }, [AllFormDetails]);

  const validateForm = (data: any) => {
    console.log("data", data);
    // Work Request Title validation
    if (!data.workRequestTitle?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Request Title is required",
      });
      return false;
    }

    if (data.workRequestTitle.length > 200) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Request Title cannot exceed 200 characters",
      });
      return false;
    }

    // Site Selection validation
    if (!data.siteId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Site Selection is required",
      });
      return false;
    }

    // Request For validation
    if (!data.requestFor) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Request For is required",
      });
      return false;
    }

    // Planned Start Date validation
    if (!data.plannedStartDate) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Planned Start Date is required",
      });
      return false;
    }

    // Planned End Date validation
    if (!data.plannedEndDate) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Planned End Date is required",
      });
      return false;
    }

    // Date range validation
    if (data.plannedStartDate && data.plannedEndDate) {
      if (dayjs(data.plannedEndDate).isBefore(dayjs(data.plannedStartDate))) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Planned End Date must be after Planned Start Date",
        });
        return false;
      }
    }

    // Approval Required validation
    if (!data.approvalRequired) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please specify if approval is required",
      });
      return false;
    }

    return true;
  };

  const submitForm = async (data: WorkRequestFormData) => {
    console.log("data", data);
    if (!validateForm(data)) {
      console.log("Validation failed");
      return;
    }

    // Set approvals data in form
    setValue("approvalsData", JSON.stringify(approvals));
    setValue("isStrictApproval", isStrictApproval.toString());

    if (data.approvalRequired === "true") {
      setValue("isStrictApproval", isStrictApproval.toString());
    } else {
      setValue("isStrictApproval", "false");
    }
    // Only block navigation if approval is required ("true") but no approvals set
    if (data.approvalRequired === "true" && approvals?.data?.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Approval Required",
        text: "Please set the required approvals before proceeding to the next step.",
      });
      return;
    }

    const payload = {
      ...data,
      ...(data.approvalRequired === "true" && { isStrictApproval: Boolean(isStrictApproval) }),
      approvalsData: JSON.stringify(approvals),
      step: "generalDetails",
    };

    console.log("General Details Payload:", payload);
    props.next(payload);
  };

  return (
    <>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <div className="card">
          <div className="card-body">
            {/* <div className="card-title">
              <h5>🧩 1. General Information Section</h5>
              <p className="text-muted">Enter the basic information for your work request as per FSD specifications</p>
            </div> */}

            <Row className="gy-4">
              {/* Work Request # - Auto-generated (FSD Field 1) */}
              {/* <Col lg="6">
                <FormInput
                  name="workRequestNumber"
                  label="Work Request #"
                  required={true}
                  placeholder="Auto-generated"
                  disabled={true}
                  tooltipText="System-generated unique identifier"
                />
              </Col> */}

              {/* Work Request Title - Mandatory (FSD Field 2) */}
              <Col lg="6">
                <FormInput
                  name="workRequestTitle"
                  label="Work Request Title"
                  required={true}
                  placeholder="Enter work request title"
                  maxLength={200}
                  tooltipText="Title of the work request"
                />
              </Col>

              <Col lg="6">
                <FormSelect
                  name="siteId"
                  label="Site Selection"
                  options={sitesOptions}
                  required={true}
                  placeholder="Select Site"
                  // loading={loading.sites}
                  tooltipText="Select site from the predefined master data"
                  onChange={(value) => {
                    const selectedSite = sitesOptions.find(
                      (option) => option.value === value
                    );
                    setValue("siteName", selectedSite?.label || "");
                  }}
                />
              </Col>

              {/* Description - Optional (FSD Field 3) */}
              <Col lg="12">
                <FormTextArea
                  name="description"
                  label="Description"
                  placeholder="Enter description or purpose of the work request (Optional)"
                  maxLength={500}
                  rows={3}
                // tooltipText="Description or purpose of the work request"
                />
              </Col>

              {/* Site Selection - Mandatory (FSD Field 4) */}

              {/* Project Selection - Optional (FSD Field 5) */}
              <Col lg="6">
                <FormSelect
                  name="projectId"
                  label="Project Selection"
                  options={projectOptions}
                  placeholder="Select Project (Optional)"
                  // loading={loading.projects}
                  disabled={!siteIdWatch || siteIdWatch === ""}
                  tooltipText="List of projects accessible to the user"
                  onChange={handleProjectChange}
                />
              </Col>

              {/* Request For - Mandatory Enum (FSD Field 6) */}
              <Col lg="6">
                <FormSelect
                  name="requestFor"
                  label="Request For"
                  options={requestForOptions}
                  required={true}
                  placeholder="Select Request Type"
                  tooltipText="Construction Work / Service / Maintenance / Others"
                />
              </Col>

              {/* Approval Required - Mandatory Boolean (FSD Field 9) */}
              <Col lg="6">
                <FormSelect
                  name="approvalRequired"
                  label="Approval Required ?"
                  options={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  placeholder="Select"
                  disabled={!siteIdWatch}
                  required={true}
                  tooltipText="If Yes, configure approval hierarchy as per reference path"
                />

                {/* Hidden field for approvals data */}
                <div style={{ display: "none" }}>
                  <FormInput name="approvalsData" label="" type="text" />
                </div>

                {/* Set/View Approvals Link */}
                {approvalRequiredWatch === "true" && (
                  <Popover
                    content={
                      AllFormDetails.type === "edit"
                        ? "Click here to view approval hierarchy"
                        : "Click here to set required approvals"
                    }
                    title="Approval Required"
                  >
                    <a
                      href="#"
                      className="success"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!siteIdWatch) {
                          message.error("Please select a site first");
                          return;
                        }

                        if (AllFormDetails.type === "edit") {
                          // In edit mode, open view-only drawer
                          setShowViewApprovals(true);
                        } else {
                          // In create mode, open editable drawer
                          setApprovals((pre) => {
                            return {
                              ...pre,
                              open: true,
                            };
                          });
                        }
                      }}
                    >
                      <EditOutlined /> {AllFormDetails.type === "edit" ? "View Approvals" : "Set Approvals"} &nbsp;
                      {approvals?.data?.length === 0 ? (
                        <span className="mandatory">
                          {AllFormDetails.type === "edit" ? "No approvals configured" : "Please set approvals"}
                        </span>
                      ) : (
                        <span className="text-success">
                          ✓ {approvals.data.length} approval(s) configured
                        </span>
                      )}
                    </a>
                  </Popover>
                )}
              </Col>

              {/* Planned Start Date - Mandatory (FSD Field 7) */}
              <Col lg="6">
                <FormDatePicker
                  name="plannedStartDate"
                  label="Planned Start Date"
                  required={true}
                  placeholder="Select Planned Start Date"
                // tooltipText="Tentative planned start"
                // disabledDate={(current: any) => current && current < dayjs().startOf('day')}
                />
              </Col>

              {/* Planned End Date - Mandatory (FSD Field 8) */}
              <Col lg="6">
                <FormDatePicker
                  name="plannedEndDate"
                  label="Planned End Date"
                  required={true}
                  placeholder="Select Planned End Date"
                // tooltipText="Tentative planned end"
                // disabledDate={(current: any) => {
                //   const startDate = plannedStartDateWatch;
                //   return current && (current < dayjs().startOf('day') ||
                //     (startDate && current < dayjs(startDate).startOf('day')));
                // }}
                />
              </Col>

              {/* Approval Hierarchy Note */}
              {/* {approvalRequiredWatch === "true" && (
                <Col lg="12">
                  <div className="alert alert-info">
                    <strong>Note:</strong> This work request will require
                    approval. The approval hierarchy will be configured based on
                    the selected site and project.
                    <br />
                    <small className="text-muted">
                      Reference: Purchase Order General Details for approval
                      configuration
                    </small>
                  </div>
                </Col>
              )} */}
            </Row>
          </div>
        </div>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit">
                Next
              </Button>
            </li>
          </ul>
        </div>
      </form>

      {/* Work Order Approval Drawer - Editable (Create/Duplicate Mode) */}
      {approvals.open && (
        <WorkRequestApprovalDrawer
          open={approvals.open}
          siteId={parseInt(siteIdWatch) || 0}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
            setApprovals({
              open: false,
              data: data,
            });
            setIsStrictApproval(isStrictApprovalFromDrawer);
          }}
          initialStrictApproval={isStrictApproval}
          setApprovalSubmissionStatus={(status: boolean) => { }}
          approvalData={approvals.data}
        />
      )}

      {/* Work Order Approval View Drawer - Read Only (Edit Mode) */}
      {showViewApprovals && (
        <WorkRequestApprovalViewDrawer
          open={showViewApprovals}
          onClose={() => setShowViewApprovals(false)}
          approvalData={approvals.data}
          title="View Work Request Approval Hierarchy"
        />
      )}
    </>
  );
};

export default WorkRequestGeneralDetails;
